import { useState } from 'react';

import type {
  AccountStatusFilter,
  Master,
  MasterChanges,
  NewMaster,
} from '../../api/monitor';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { useMasters } from '../../hooks/useMasters';
import { cyclesSummary, formatCnpj, masterMatches, sourceLabel } from '../../lib/masters';
import { MasterModal } from './MasterModal';

/** Filtro de situação; "Ativas" primeiro porque é o padrão. */
const STATUS_CHIPS: Array<{ value: AccountStatusFilter; label: string }> = [
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
  { value: 'all', label: 'Todas' },
];

/**
 * Tela de Masters: o cadastro de masters e o ciclo de faturamento de cada uma
 * por operadora (`master_billing_cycles`).
 *
 * O ciclo é o que define a janela das rotinas diárias (importação da Claro,
 * conferência da Vivo, franquia na sincronização de linha) — antes ele vivia no
 * `billing-cycles.yml` e só mudava com deploy. As masters do SGC continuam
 * chegando pela sincronização diária; as cadastradas aqui ficam como
 * "Cadastrada no SICOP" e não são tocadas por ela.
 */
export function MastersScreen() {
  const controller = useMasters();
  const { masters, operators, status, loading, error, saving } = controller;
  const { toast, show } = useToast();
  const [editing, setEditing] = useState<Master | null>(null);
  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState('');

  const visible = masters.filter((master) => masterMatches(master, query));

  const handleCreate = async (payload: NewMaster) => {
    const created = await controller.create(payload);
    setCreating(false);
    show(`Master cadastrada: ${created?.name ?? payload.name}`);
  };

  const handleUpdate = async (changes: MasterChanges) => {
    if (!editing) return;

    await controller.update(editing.id, changes);
    const what =
      changes.active === false && editing.active
        ? 'Master inativada'
        : changes.active === true && !editing.active
          ? 'Master reativada'
          : 'Master atualizada';
    setEditing(null);
    show(`${what}: ${changes.name ?? editing.name}`);
  };

  if (loading && masters.length === 0 && !error) {
    return <LoadingCard message="Carregando as masters…" />;
  }

  if (error && masters.length === 0) {
    return (
      <ErrorCard
        title="Não foi possível carregar as masters."
        detail={error}
        onRetry={controller.reload}
      />
    );
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        <div
          className="flex gap-1.5"
          role="group"
          aria-label="Situação da master"
        >
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => controller.setStatus(chip.value)}
              aria-pressed={chip.value === status}
              className={`rounded-full border px-3.5 py-2 text-[12.5px] font-bold ${
                chip.value === status
                  ? 'border-navy-800 bg-navy-800 text-white'
                  : 'border-line-300 text-ink-600 bg-white'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar master…"
          aria-label="Buscar master"
          className="border-line-300 focus:border-navy-800 w-64 rounded-[10px] border bg-white px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#00244733]"
        />

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setCreating(true)}
          disabled={operators.length === 0}
          className="bg-navy-800 hover:bg-navy-950 shrink-0 rounded-[10px] px-4 py-2.5 text-[13px] font-extrabold text-white disabled:opacity-60"
        >
          + Nova master
        </button>
      </div>

      {error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          {error}
        </div>
      ) : null}

      <div
        className={`border-line-200 overflow-hidden rounded-[14px] border bg-white ${loading ? 'opacity-60' : ''}`}
      >
        {masters.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nenhuma master nesta situação.
          </div>
        ) : visible.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nenhuma master encontrada para “{query.trim()}”.
          </div>
        ) : (
          visible.map((master) => (
            <div
              key={master.id}
              className="border-surface-200 flex items-center gap-2.5 border-b px-4 py-3 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-extrabold">
                  {master.name}
                </div>
                <div className="text-ink-600 mt-px truncate text-[11.5px]">
                  CNPJ {formatCnpj(master.cnpj)}
                  {master.code ? ` · código ${master.code}` : ''} ·{' '}
                  {sourceLabel(master.source)}
                  {master.source === 'sgc'
                    ? ` (id ${master.externalId})`
                    : ''}{' '}
                  · {master.accountsCount}{' '}
                  {master.accountsCount === 1 ? 'conta' : 'contas'}
                </div>
                <div
                  className={`mt-0.5 truncate text-[11.5px] font-bold ${
                    master.cycles.length ? 'text-navy-800' : 'text-warn-700'
                  }`}
                >
                  {cyclesSummary(master)}
                </div>
              </div>

              <div
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${
                  master.active
                    ? 'bg-ok-100 text-ok-600'
                    : 'bg-danger-100 text-danger-600'
                }`}
              >
                {master.active ? 'Ativa' : 'Inativa'}
              </div>

              <button
                type="button"
                onClick={() => setEditing(master)}
                className="border-line-300 text-navy-800 hover:border-navy-800 hover:bg-surface-100 shrink-0 rounded-lg border bg-white px-3 py-1.5 text-[12px] font-bold"
              >
                Editar<span className="sr-only"> a master {master.name}</span>
              </button>
            </div>
          ))
        )}
      </div>

      {creating || editing ? (
        <MasterModal
          master={editing}
          operators={operators}
          saving={saving}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
        />
      ) : null}

      <Toast message={toast} />
    </>
  );
}
